import Vue from 'vue';
import Vuex from 'vuex';
import { api } from './api';

import GatewayFileActions from './components/gateway-file/gateway-file.actions';
import GatewayInstructionActions from './components/gateway-instruction/gateway-instruction.actions';
import GatewayInstructionMutations from './components/gateway-instruction/gateway-instruction.mutations';
import GatewayActions from './components/gateway/gateway.actions';
import GatewayMutations from './components/gateway/gateway.mutations';

Vue.use(Vuex);

export default new Vuex.Store({
    state: {
        debug: true,
        appTitle: null,
        forms: {
            gateway: {
                currentSelectedInput: null,
            }
        },
        activeCustomer: {
            gateways: [],
            processes: [],
        },
        activeCustomerGateway: {},
        activeGatewayInstructions: [],
        activeGateway: {},
        gateways: [],
        encodings: [],
        metadatas: [],
        customers: [],
        gatewayInstructions: [],
        sampleFileTestResult: null,
        focusedTemplateConfigurations: {}
    },
    mutations: {
        setAppTitle(state, newAppTitle) {
            state.appTitle = newAppTitle;
        },
        removeAppTitle(state) {
            state.appTitle = null;
        },
        onSuccess() {
            console.log('SUCCESS!');
            return;
        },
        onFailure() {
            console.log('FAILURE!');
            return;
        },
        setCustomers(state, customers) {

            state.customers = customers.map(customer => ({
                id: customer.id,
                name: customer.name,
                gatewayFilesCount: customer.gateway_files_count,
            }));
        },
        setActiveCustomerGateway(state, gatewayFile) {
            state.activeCustomerGateway = {
                id: gatewayFile ? gatewayFile.id : null,
                name: gatewayFile ? gatewayFile.name : null,
                comment: gatewayFile ? gatewayFile.comment : null,
                encodingId: gatewayFile ? gatewayFile.encoding_id : null,
                maxMediaDownloadCount: (
                        gatewayFile &&
                        Number.isInteger(parseFloat(gatewayFile.max_media_download_count)) === true &&
                        gatewayFile.maxMediaDownloadCount !== ''
                    ) ? parseFloat(gatewayFile.max_media_download_count) : null,
                downloadBatchLength: gatewayFile && gatewayFile.download_batch_length ? parseInt(gatewayFile.download_batch_length) : null,
                sharedWithIds: gatewayFile ? gatewayFile.shared_with_ids : [],
                sharedWith: gatewayFile ? gatewayFile.shared_with : [],
                createdBy: {
                    id: gatewayFile && gatewayFile.created_by ? gatewayFile.created_by.id : null,
                    name: gatewayFile && gatewayFile.created_by ? gatewayFile.created_by.name : null,
                },
                gateway: {
                    id: gatewayFile && gatewayFile.gateway ? gatewayFile.gateway.id : null,
                    name: gatewayFile && gatewayFile.gateway ? gatewayFile.gateway.name : null,
                    metadatas: gatewayFile && gatewayFile.gateway
                        ? gatewayFile.gateway.metadatas.map(metadata => ({
                            id: metadata.id,
                            code: metadata.code,
                            description: metadata.description,
                            isSelectable: metadata.is_selectable,
                            conflicts: Array.isArray(gatewayFile.conflicts)
                                ? gatewayFile
                                    .conflicts
                                    .filter(conflict => conflict.metadata_id === metadata.id)
                                    .map(conflict => ({
                                        customerId: conflict.customer_id,
                                        customerName: conflict.customer_name,
                                        metadataId: conflict.metadata_id,
                                        metadataValue: conflict.metadata_value,
                                        gatewayFileId: conflict.gateway_file_id 
                                    }))
                                : []
                        }))
                        : [],
                },
                metadataValues: gatewayFile
                    ? gatewayFile.metadata_values.map(mv => ({
                        id: mv.metadata_id,
                        value: mv.value,
                    }))
                    : [],
            };
        },
        setActiveCustomer(state, customer) {
            state.activeCustomer = {
                ...customer,
                gatewayFilesCount: customer.gateway_files_count,
                gateways: [],
                processes: [],
            };
        },
        setActiveCustomerGateways(state, customerGateways) {

            state.activeCustomer.gateways = customerGateways.map(file => ({
                id: file.id,
                name: file.name,
                comment: file.comment,
                encodingId: file.encoding_id || -1,
                sharedWithIds: file.shared_with_ids,
                sharedWith: file.shared_with,
                createdBy: {
                    id: file.created_by.id,
                    name: file.created_by.name,
                },
                gateway: {
                    id: file.gateway.id,
                    name: file.gateway.name,
                    metadatas: file.gateway.metadatas.map(metadata => ({
                        id: metadata.id,
                        code: metadata.code,
                        description: metadata.description,
                        isSelectable: metadata.is_selectable,
                    })),
                    cronExpression: file.gateway.cron_expression,
                },
                metadataValues: file.metadata_values.map(mv => ({
                    id: mv.metadata_id,
                    value: mv.value,
                })),
                lastProcess: file && file.last_process ? {
                    id: file.last_process.id,
                    startedAt: file.last_process.started_at,
                    endedAt: file.last_process.ended_at,
                    currentState: file.last_process.current_state,
                    listingsCount: file.last_process.listings_count,
                }
                : null,
                hasConflicts: file && file.conflicts && Array.isArray(file.conflicts) && file.conflicts.length > 0
            }));
        },
        setActiveCustomerGatewaysProcesses(state, processes) {
            state.activeCustomer.processes = processes;
        },
        updateGatewayProcess(state, process) {
            const index = state.activeCustomer.processes.findIndex(item => item.gatewayFile.id === process.gatewayFile.id);
            if (index >= 0) {
                state.activeCustomer.processes.splice(index, 1, process);
            } else {
                state.activeCustomer.processes.push(process);
            }
        },
        updateActiveGatewayInstructions(state, data) {
            state.activeGatewayInstructions = data;
        },
        initFocusedTemplateConfigurations(state, customerId) {
            state.focusedTemplateConfigurations[customerId] = state.focusedTemplateConfigurations[customerId]
                ? state.focusedTemplateConfigurations[customerId]
                : [];
        },
        toggleFocusedTemplateConfigurations(state, {customerId, templateId}) {
            state.focusedTemplateConfigurations[customerId] = state.focusedTemplateConfigurations[customerId].includes(templateId)
                ? [...state.focusedTemplateConfigurations[customerId].filter(item => item !== templateId)]
                : [...new Set([...state.focusedTemplateConfigurations[customerId], templateId])];
        },
        closeAllFocusedTemplateConfigurations(state, customerId) {
            state.focusedTemplateConfigurations[customerId] = [];
        },
        ...GatewayMutations,
        ...GatewayInstructionMutations,
    },
    actions: {
        async getCustomerGateways({ commit, dispatch }, customerId) {
            const gateways = await api.gatewaysFiles.getByCustomer(customerId);

            // Non-blocking fetch processes
            dispatch('getCustomerGatewaysProcesses', customerId)
                .catch(() => {
                    // FIXME: silent error
                });

            return commit('setActiveCustomerGateways', gateways);
        },
        async getCustomerGatewaysProcesses({ commit }, customerId) {
            const processes = await api.customers.getGatewaysProcesses(customerId);
            return commit('setActiveCustomerGatewaysProcesses', processes);
        },
        async getCustomerGatewayById({ commit }, { customerId, gatewayFileId }) {
            const gateway = await api.gatewaysFiles.getById(customerId, gatewayFileId);
            return commit('setActiveCustomerGateway', gateway);
        },
        async getCustomerById({commit}, customerId) {
            const customer = await api.customers.getById(customerId);
            return commit('setActiveCustomer', customer);
        },
        async getCustomers({commit}) {
            // TODO: optimize this to avoid complete list fetch ?
            const customers = await api.customers.fetch({ pageSize: 0 });
            return commit('setCustomers', customers.items);
        },
        ...GatewayActions,
        ...GatewayFileActions,
        ...GatewayInstructionActions,
    },
    getters: {
        appTitle: state => state.appTitle,
        activeGateway: state => state.activeGateway,
        activeCustomer: state => state.activeCustomer,
        activeCustomerGateway: state => state.activeCustomerGateway,
        activeGatewayInstructions: state => state.activeGatewayInstructions,
        metadatas: state => state.metadatas,
        gateways: state => state.gateways,
        customers: state => state.customers,
        gatewayInstructions: state => state.gatewayInstructions,
        gatewayBindings: state => state.gatewayBindings,
        sampleFileTestResult: state => state.sampleFileTestResult,
        activeCustomerProcesses: (_, getters) => getters.activeCustomer.processes, 
        focusedTemplateConfigurations: state => (customerId) => state.focusedTemplateConfigurations[customerId]
            ? state.focusedTemplateConfigurations[customerId]
            : [],
    },
});
